import React, { useState } from "react";
import { Link } from "react-router-dom";
import { DotsThree, Flag, Trash, EyeSlash, LinkSimple, PencilSimple } from "@phosphor-icons/react";
import { avatarUrl } from "@/lib/api";
import VerifiedBadge from "@/components/VerifiedBadge";

/**
 * Voxly — PostCard header.
 *
 * Renders the avatar, name + handle + relative time, plus the kebab menu.
 * Menu entries (Facebook-style):
 *   Owner:    Edit · Copy link · Delete
 *   Viewer:   Hide · Copy link · Report
 */
function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function PostHeader({ post, isOwn, onDelete, onReport, onEdit, onHide, onCopyLink }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  return (
    <header className="flex items-center gap-3 px-4 py-3" data-testid={`post-header-${post.post_id}`}>
      <Link to={`/u/${post.user_id}`} className="shrink-0 rounded-full story-ring p-[2px]">
        <img
          src={avatarUrl(post.user_picture)}
          alt={post.user_name}
          className="h-10 w-10 rounded-full object-cover border-2 border-background"
          loading="lazy"
          decoding="async"
        />
      </Link>
      <div className="flex-1 min-w-0">
        <Link
          to={`/u/${post.user_id}`}
          className="font-semibold text-sm hover:underline truncate flex items-center gap-1"
        >
          {post.user_name}
          {post.user_verified && <VerifiedBadge size={14} />}
        </Link>
        <span className="text-[11px] text-muted-foreground">
          {post.user_username && <span className="mr-1.5">@{post.user_username}</span>}
          {timeAgo(post.created_at)} ago
          {post.edited_at && <span className="ml-1 opacity-80">· edited</span>}
        </span>
      </div>
      <div className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          data-testid={`post-menu-${post.post_id}`}
          aria-label="More options — edit, delete or report"
          title="More options"
          aria-expanded={menuOpen}
          className="h-10 w-10 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 flex items-center justify-center transition active:scale-90"
        >
          <DotsThree size={26} weight="bold" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={close} />
            <div
              className="absolute right-0 top-9 z-20 w-52 rounded-2xl glass p-1 shadow-2xl"
              data-testid={`post-menu-pop-${post.post_id}`}
              role="menu"
            >
              {isOwn ? (
                <>
                  <MenuItem icon={PencilSimple} testid={`edit-post-${post.post_id}`} onClick={() => { close(); onEdit?.(); }}>
                    Edit post
                  </MenuItem>
                  <MenuItem icon={LinkSimple} testid={`copy-link-${post.post_id}`} onClick={() => { close(); onCopyLink?.(); }}>
                    Copy link
                  </MenuItem>
                  <MenuItem
                    icon={Trash}
                    testid={`delete-post-${post.post_id}`}
                    onClick={() => { close(); onDelete?.(); }}
                    destructive
                  >
                    Delete post
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem icon={EyeSlash} testid={`hide-post-${post.post_id}`} onClick={() => { close(); onHide?.(); }}>
                    Hide post
                  </MenuItem>
                  <MenuItem icon={LinkSimple} testid={`copy-link-${post.post_id}`} onClick={() => { close(); onCopyLink?.(); }}>
                    Copy link
                  </MenuItem>
                  <MenuItem icon={Flag} testid={`report-post-${post.post_id}`} onClick={() => { close(); onReport?.("post", post.post_id); }}>
                    Report post
                  </MenuItem>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
}

function MenuItem({ icon: Icon, children, onClick, testid, destructive = false }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      role="menuitem"
      className={`w-full px-3 py-2 rounded-xl flex items-center gap-2 text-sm transition ${
        destructive ? "text-destructive hover:bg-destructive/10" : "hover:bg-white/5"
      }`}
    >
      <Icon size={16} />
      {children}
    </button>
  );
}
